import './ui/styles.css';
import './ui/game.css';

import { GameSession, type RunOutcome } from './game.ts';
import { Renderer, type RenderSettings } from './render/renderer.ts';
import { Hud, formatTime } from './render/hud.ts';
import { InputController, ACTIONS, DEFAULT_KEYMAP, keyLabel, type Action } from './input/input.ts';
import { ThumbSteer } from './input/thumbSteer.ts';
import { PedalTouch } from './input/pedals.ts';
import { ProPedals } from './input/proPedals.ts';
import { DriftGauge } from './render/driftGauge.ts';
import { TunePanel } from './ui/tunePanel.ts';
import { isTuneMode, applyStoredTuning, restoreShippedHandling, isTuned } from './tune/tuning.ts';
import { EngineAudio } from './audio/engine.ts';
import { applySoundOverrides, PROFILES } from './audio/profiles.ts';
import { SoundLab } from './ui/soundLab.ts';
import { CARS, carById } from './data/cars.ts';
import { configFor } from './data/assist.ts';
import { ROUTES, listedRoutes, loadRoute, type RouteEntry } from './data/routes.ts';
import { activeSeason, greetingDue, markGreetingSeen } from './data/season.ts';
import { CHALLENGES, TARGETS, challengeCars, targetFor } from './data/challenges.ts';
import {
  creditBattleWin,
  creditPastRuns,
  creditRun,
  describe as describeChallenge,
  isLocked,
  lockOf,
  type Credit,
} from './storage/unlocks.ts';
import { loadSettings, saveSettings, type Settings } from './storage/settings.ts';
import {
  allBests,
  getBest,
  submitBest,
  markCompleted,
  saveReplay,
  compress,
  decompress,
  bestKey,
  hasBeaten,
  markBeaten,
} from './storage/bests.ts';
import { buildGhost, type GhostTrack } from './ghost.ts';
import { createTandem, tandemScore, type TandemSide, type TandemState } from './sim/tandem.ts';
import { battleVerdict } from './ui/judges.ts';
import { InputRecorder } from './sim/replay.ts';
import { CHARACTERS, characterById, type Character } from './data/characters.ts';
import { loadDecoration } from './render/decoration.ts';
import { getSprite, preload } from './render/sprites.ts';
import { drawPosterOverlay } from './ui/poster.ts';
import { flagElement } from './ui/flags.ts';
import { submitScore, fetchBoard, fetchGarage, fetchReplay, bytesToBase64, LeaderboardError } from './net/leaderboard.ts';
import {
  CATEGORY_LINES,
  formatDrift,
  formatTopSpeed,
  formatZeroTo100,
  formatTops,
  orderCars,
  placeLine,
  type GarageFilter,
  type GarageSort,
} from './ui/garage.ts';
import { checkName } from '../shared/moderation.ts';
import { isTandemBoard, type ApiMode, type GarageResponse, type LeaderboardRow } from '../shared/api.ts';
import { categoryOf } from '../shared/categories.ts';
import { SIM_VERSION, TICK_RATE } from './sim/version.ts';
import type { CarParams, Controls, RouteData, SimMode } from './sim/types.ts';

/**
 * App shell: screen routing, settings, and the bridge between the menus and a
 * run. Deliberately plain DOM -- the menus are a dozen static screens in a
 * flat, typographic design system, and a framework would cost more than it
 * returned here.
 */

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
};

type ScreenName = 'title' | 'routes' | 'intro' | 'garage' | 'settings' | 'results';

const SCREENS: Record<ScreenName, HTMLElement> = {
  title: $('screen-title'),
  routes: $('screen-routes'),
  intro: $('screen-intro'),
  garage: $('screen-garage'),
  settings: $('screen-settings'),
  results: $('screen-results'),
};

const gameEl = $('game');
// Belt and braces for iOS: during a run nothing on the race screen may start a
// text selection, whatever the CSS says.
gameEl.addEventListener('selectstart', (e) => e.preventDefault());
const canvas = $<HTMLCanvasElement>('canvas');

/**
 * Tuning mode: handling sliders over a paused run, applied live.
 *
 * Hidden for now: #tune or ?tune on the URL switches it on and shows its
 * toggle in Settings. Everything behind it is kept for when handling needs
 * adjusting again.
 */
let tuneMode = false;

function setTuneMode(on: boolean): void {
  tuneMode = on;
  settings.tuneMode = on;
  saveSettings(settings);
  if (on) applyStoredTuning();
  else restoreShippedHandling();
  $('btn-tune').hidden = !on;
}

const settings: Settings = loadSettings();
// Sound lab changes are presentation only, so they apply everywhere.
applySoundOverrides();
let currentEntry: RouteEntry = ROUTES[0];
let currentRoute: RouteData | null = null;
// Drift Run is the game's main mode: it is listed first and chosen by default.
let currentMode: SimMode = 'driftRun';
let session: GameSession | null = null;
let lastOutcome: RunOutcome | null = null;
/** The route page's modes: the two run modes, and tandem battles. */
type PageMode = SimMode | 'tandem';
/** Which board the route screen is showing. */
let boardMode: PageMode = 'driftRun';
/** The mode last driven: the route page reopens on it. */
let lastPageMode: PageMode = 'driftRun';

/**
 * A tandem battle in progress. Against the house: two runs, the player chasing
 * then leading, both judged out of 100, and one more time if the totals are
 * within 2%. Chasing a posted run: one run, not ranked.
 */
interface Battle {
  kind: 'house' | 'chase';
  /** Against the house: which character. */
  rival?: Character;
  /** The other driver's name. */
  name: string;
  skill: number;
  run: 1 | 2;
  /** One-more-time rounds so far. */
  omt: number;
  youChase: number;
  theyLead: number;
  youLead: number;
  theyChase: number;
  /** Both runs' time, for the board. */
  timeMs: number;
  outcome?: 'win' | 'loss' | 'omt';
  /** Each run's judging, both drivers, for the judges' words: [run 1, run 2]. */
  runs: { you: TandemSide; them: TandemSide }[];
  /** Each earlier round's totals that went to One More Time, [yours, theirs]. */
  rounds: [number, number][];
  /** Chasing a posted run: its inputs and car. */
  leader?: { inputs: InputRecorder; car: CarParams };
}
let battle: Battle | null = null;
/**
 * Which cars a board covers: those in the selected car's category -- the
 * fair fight, so the board the game opens on -- the selected car alone, or
 * every car.
 */
type BoardScope = 'category' | 'car' | 'all';
/** Which cars the route page's board covers. */
let boardCar: BoardScope = 'category';
/** Row id of the score just posted, so it can be highlighted on the board. */
let myLastRowId: string | null = null;

/**
 * The leaderboard run picked to race against. It belongs to one board: a
 * ghost from the Drift Run board is not raced in Time Attack.
 */
interface GhostPick {
  row: LeaderboardRow;
  board: ApiMode;
  routeId: string;
  routeVersion: number;
  /** Fetched as soon as it is picked, so Go does not wait on the network. */
  input: Promise<Uint8Array>;
}
let ghostPick: GhostPick | null = null;
/** The ghost the last run raced, for the results. */
let runGhost: { row: LeaderboardRow; track: GhostTrack } | null = null;

const renderer = new Renderer(canvas);
const hud = new Hud({
  root: $('game'),
  comboLabel: $('hud-combo-label'),
  comboValue: $('hud-combo'),
  scoreLabel: $('hud-score-label'),
  scoreValue: $('hud-score'),
  driftLabel: $('hud-drift-label'),
  driftValue: $('hud-drift'),
  speedValue: $('hud-speed'),
  angleValue: $('hud-angle'),
  pace: $('pace'),
  progressFill: $('progress-fill'),
  progressLabel: $('progress-label'),
  flash: $('hud-flash'),
  bankPop: $('bank-pop'),
  zoneChip: $('zone-chip'),
  zoneMarks: $('zone-marks'),
  gauge: new DriftGauge($('drift-gauge')),
  ghostGap: $('ghost-gap'),
  ghostMark: $('ghost-mark'),
  tandem: $('tandem-hud'),
  tandemPop: $('tandem-pop'),
});

const input = new InputController();
input.setKeymap(settings.keymap);

/** Touch anywhere during a run and slide sideways to steer. */
const thumb = new ThumbSteer($('steer-surface'), (value) => input.setTouchSteer(value));
thumb.setSensitivity(settings.steerSensitivity);

/** Drift Run's throttle controls: tap the left half to drift, hold the right. */
const proPedals = new ProPedals($('pro-surface'), {
  onThrottle: (held) => input.setTouchThrottle(held),
  onBrake: (held) => input.setTouchBrake(held),
});

const pedals = new PedalTouch($('pedal-surface'), {
  onThrottle: (held) => input.setTouchThrottle(held),
  onDrift: (held) => (held ? input.pressDrift() : input.releaseDrift()),
});

hud.setDriftPad(pedals.driftZone);

/** Which controls the current run uses. */
let currentControls: Controls = 'steer';

/** Runs that are not saved or posted, and why; null when the run counts. */
function unrankedReason(): string | null {
  if (tuneMode) return 'Tuning mode. This run is not saved as a best or posted to the leaderboard.';
  if (currentMode === 'driftRun' && currentControls === 'steer') {
    return 'Steered Drift Run. This run is not saved or posted: the leaderboard is for the throttle controls.';
  }
  return null;
}

// --- Input mode -------------------------------------------------------------

/**
 * Whether to show touch hints or key hints.
 *
 * Decided on the device's primary pointer at boot, then switched on real use:
 * a touch means touch, a bound key means keyboard.
 */
function setInputMode(mode: 'touch' | 'keyboard'): void {
  gameEl.dataset.input = mode;
}

setInputMode(window.matchMedia?.('(pointer: coarse)').matches ? 'touch' : 'keyboard');

// --- Screen routing ---------------------------------------------------------

const soundLab = new SoundLab($('sound-lab'), () => {
  soundLab.close();
  showScreen('settings');
});

function showScreen(name: ScreenName): void {
  if (!$('sound-lab').hidden) soundLab.close();
  for (const key of Object.keys(SCREENS) as ScreenName[]) {
    SCREENS[key].hidden = key !== name;
  }
  // Each screen scrolls by itself and remembers where it was left, so the
  // results after a second run opened wherever the last ones were scrolled to
  // -- usually down at the leaderboard, with the run's own score out of sight.
  SCREENS[name].scrollTop = 0;
  gameEl.hidden = true;
}

function showGame(): void {
  for (const key of Object.keys(SCREENS) as ScreenName[]) SCREENS[key].hidden = true;
  gameEl.hidden = false;
  // The canvas has no size until it is displayed, so measure now, not at boot.
  renderer.resize();
}

// --- Route list -------------------------------------------------------------

function buildRouteList(): void {
  const list = $('route-list');
  list.replaceChildren(
    ...listedRoutes().map((entry) => {
      const button = document.createElement('button');
      button.className = 'card';

      const left = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'card__name';
      name.textContent = entry.name;
      const meta = document.createElement('div');
      meta.className = 'card__meta';

      // Every route is open from the start, and the card always carries the
      // route's own line rather than a best time.
      meta.textContent = entry.blurb;

      left.append(name, meta);
      button.append(left, flagElement(entry.country, entry.location));
      button.addEventListener('click', () => void openIntro(entry));
      return button;
    }),
  );
}

// --- Route intro / course map ----------------------------------------------

async function openIntro(entry: RouteEntry): Promise<void> {
  currentEntry = entry;
  currentRoute = await loadRoute(entry);
  if (ghostPick && ghostPick.routeId !== currentRoute.id) setGhostPick(null);
  $('intro-name').textContent = entry.name;
  $('intro-location').textContent = entry.location;
  // Routes built from someone else's data say whose. Required by the ODbL for
  // the OpenStreetMap ones, and only fair for anything else.
  const credit = currentRoute.attribution ?? entry.attribution ?? '';
  $('intro-credit').textContent = credit;
  $('intro-credit').hidden = credit === '';

  const route = currentRoute;

  showPoster(route);
  showIntroCar();
  // The board opens on the mode last driven, so coming back from a Drift Run
  // shows the drift scores.
  battle = null;
  setBoardMode(lastPageMode);
  showScreen('intro');

  // Scenery and car art load in the background. Neither blocks the drive
  // button, and neither is required for the route to be playable.
  void loadDecoration(route.decoration).then((deco) => renderer.setDecoration(deco));
  renderer.prepareWorld(route);
  void preload([carById(settings.carId).sprite?.path, entry.poster?.src]).then(() => {
    // The poster arrives after the screen is up; swap it in if we are still here.
    if (currentEntry === entry && !SCREENS.intro.hidden) showPoster(route);
  });
}

// --- Leaderboard ------------------------------------------------------------

/**
 * The mode picked on a route's page. It is also the leaderboard shown there:
 * choosing Drift Run shows the drift scores, with a label saying so.
 */
function setBoardCar(which: BoardScope): void {
  boardCar = which;
  paintScope('board', which);
  void refreshBoard();
}

/** The car a board is asked for: a category, an id for one car's records, or every car. */
function scopeCar(scope: BoardScope): string {
  if (scope === 'car') return settings.carId;
  if (scope === 'category') return categoryOf(settings.carId)?.id ?? 'all';
  return 'all';
}

function boardCarId(): string {
  return scopeCar(boardCar);
}

/** Label and press a board's three buttons for the selected car. */
function paintScope(prefix: 'board' | 'result', scope: BoardScope): void {
  $(`btn-${prefix}-category`).textContent = categoryOf(settings.carId)?.name ?? 'Category';
  $(`btn-${prefix}-car`).textContent = carById(settings.carId).name;
  $(`btn-${prefix}-category`).setAttribute('aria-pressed', String(scope === 'category'));
  $(`btn-${prefix}-car`).setAttribute('aria-pressed', String(scope === 'car'));
  $(`btn-${prefix}-all`).setAttribute('aria-pressed', String(scope === 'all'));
}

/** What an empty board says. */
function emptyBoard(scope: BoardScope, noun: string): string {
  if (scope === 'car') return `No ${noun} in the ${carById(settings.carId).name} yet. Be first.`;
  const category = categoryOf(settings.carId);
  if (scope === 'category' && category) return `No ${noun} in ${category.name} yet. Be first.`;
  return `No ${noun} posted yet. Be first.`;
}

function setBoardMode(mode: PageMode): void {
  boardMode = mode;
  $('btn-tandem').setAttribute('aria-pressed', String(mode === 'tandem'));
  $('tandem-row').hidden = mode !== 'tandem';
  $('chase-board').hidden = mode !== 'tandem';
  if (mode === 'tandem') {
    buildRivals();
    void refreshChaseBoard();
  }
  paintScope('board', boardCar);
  const pro = settings.timeAttackLevel === 'pro';
  $('btn-time-attack').setAttribute('aria-pressed', String(mode === 'timeAttack'));
  $('btn-drift-run').setAttribute('aria-pressed', String(mode === 'driftRun'));
  $('level-row').hidden = mode !== 'timeAttack';
  $('btn-level-easy').setAttribute('aria-pressed', String(!pro));
  $('btn-level-pro').setAttribute('aria-pressed', String(pro));
  $('level-note').textContent = pro ? 'You work the gas and brake.' : 'The car brakes for corners.';
  $('board-title').textContent = `Leaderboard · ${boardTitle(pageBoard())}`;
  // A ghost belongs to the board it was picked from.
  if (ghostPick && ghostPick.board !== pageBoard()) setGhostPick(null);
  showIntroChallenge();
  void refreshBoard();
}

function setTimeAttackLevel(level: 'easy' | 'pro'): void {
  settings.timeAttackLevel = level;
  saveSettings(settings);
  setBoardMode(boardMode);
}

/** The board a run belongs to: Time Attack on pedals has its own. */
function boardFor(mode: SimMode, controls: Controls): ApiMode {
  return mode === 'timeAttack' && controls === 'pedals' ? 'timeAttackPro' : mode;
}

/** The board the route page is showing: its mode, and for Time Attack the level. */
function pageBoard(): ApiMode {
  if (boardMode === 'tandem') return rivalBoard(characterById(settings.tandemRival));
  return boardFor(boardMode, settings.timeAttackLevel === 'pro' ? 'pedals' : 'steer');
}

function boardTitle(board: ApiMode): string {
  if (isTandemBoard(board)) {
    const rival = CHARACTERS.find((c) => rivalBoard(c) === board);
    return rival ? `Drift Tandem · vs ${rival.name}` : 'Drift Tandem';
  }
  return board === 'driftRun' ? 'Drift Run' : board === 'timeAttackPro' ? 'Time Attack · Pro' : 'Time Attack · Easy';
}

/**
 * Fetch and draw the board for the current route and mode.
 *
 * Every failure here is non-fatal by design. The board is the only part of the
 * game that needs the network, so an outage shows a line of text and the player
 * carries on driving.
 */
async function refreshBoard(): Promise<void> {
  const route = currentRoute;
  const container = $('board-rows');
  if (!route) return;
  // Taken now: the player can switch mode, level or car while this is in flight.
  const mode = boardMode;
  const key = pageBoard();
  const car = boardCarId();

  container.replaceChildren(caption('Loading…'));

  try {
    const board = await fetchBoard(
      route.id,
      key,
      car,
      route.version,
      SIM_VERSION,
    );
    if (key !== pageBoard() || car !== boardCarId()) return;
    if (board.rows.length === 0) {
      container.replaceChildren(caption(emptyBoard(boardCar, 'times')));
      return;
    }
    container.replaceChildren(...board.rows.map((row) => boardRow(row, mode, key)));
  } catch (err) {
    const message =
      err instanceof LeaderboardError ? err.message : 'Could not reach the leaderboard.';
    container.replaceChildren(caption(message));
  }
}

/** One leaderboard row, formatted for the board it is on -- not for whichever tab the route page last showed. */
function boardRow(row: LeaderboardRow, mode: PageMode, board: ApiMode, action: 'ghost' | 'chase' = 'ghost'): HTMLElement {
  const el = document.createElement('div');
  el.className = row.id === myLastRowId ? 'board__row board__row--me' : 'board__row';

  const rank = document.createElement('span');
  rank.className = 'board__rank';
  rank.textContent = String(row.rank);

  const name = document.createElement('span');
  name.className = 'board__name';
  name.textContent = row.playerName;
  const car = document.createElement('span');
  car.className = 'board__car';
  car.textContent = carById(row.carId).name;
  name.appendChild(car);

  const value = document.createElement('span');
  value.className = 'board__value';
  // Time Attack shows only a time; Drift Run shows points, with the time as
  // the secondary value, exactly as the brief specifies.
  value.textContent =
    isTandemBoard(board)
      ? String(row.points)
      : mode === 'timeAttack'
        ? formatTime(row.timeMs / 1000)
        : row.points.toLocaleString('en-GB');
  if (isTandemBoard(board)) {
    // A battle total out of 200, and whether it beat the house.
    const result = document.createElement('span');
    result.className = 'board__car';
    result.style.textAlign = 'right';
    result.textContent = row.grade === 'W' ? 'Win' : 'Loss';
    value.appendChild(result);
  } else if (mode === 'driftRun') {
    const time = document.createElement('span');
    time.className = 'board__car';
    time.style.textAlign = 'right';
    time.textContent = formatTime(row.timeMs / 1000);
    value.appendChild(time);
  }

  el.append(rank, name, value);

  // Chase this run in a tandem: its stored inputs lead.
  if (action === 'chase') {
    const chase = document.createElement('button');
    chase.type = 'button';
    chase.className = 'board__ghost';
    chase.textContent = 'Chase';
    chase.setAttribute('aria-label', `Chase ${row.playerName}'s run`);
    chase.addEventListener('click', () => void startChase(row));
    el.append(chase);
    return el;
  }

  // Race this run: its stored inputs, driven again beside the player.
  if (row.hasReplay) {
    const ghost = document.createElement('button');
    ghost.type = 'button';
    ghost.className = 'board__ghost';
    ghost.textContent = 'Ghost';
    ghost.dataset.ghostId = row.id;
    ghost.setAttribute('aria-label', `Race ${row.playerName}'s ghost`);
    ghost.setAttribute('aria-pressed', String(ghostPick?.row.id === row.id));
    ghost.addEventListener('click', () => toggleGhost(row, board));
    el.append(ghost);
  } else {
    el.append(document.createElement('span'));
  }
  return el;
}

// --- Tandem ------------------------------------------------------------------

/** Posted Drift Runs to chase, under the tandem board. */
async function refreshChaseBoard(): Promise<void> {
  const route = currentRoute;
  const box = $('chase-rows');
  if (!route) return;
  box.replaceChildren(caption('Loading…'));
  try {
    const board = await fetchBoard(route.id, 'driftRun', 'all', route.version, SIM_VERSION);
    if (boardMode !== 'tandem') return;
    const rows = board.rows.filter((row) => row.hasReplay);
    box.replaceChildren(
      ...(rows.length === 0
        ? [caption('No Drift Runs posted yet.')]
        : rows.map((row) => boardRow(row, 'driftRun', 'driftRun', 'chase'))),
    );
  } catch (err) {
    box.replaceChildren(caption(err instanceof LeaderboardError ? err.message : 'Could not reach the leaderboard.'));
  }
}

/** A character's own tandem board. */
function rivalBoard(c: Character): ApiMode {
  return `tandem-${c.id}` as ApiMode;
}

/** The four house characters, to pick from on the route page. */
function buildRivals(): void {
  const route = currentRoute;
  const box = $('rivals');
  box.replaceChildren(
    ...CHARACTERS.map((c) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'rival';
      card.setAttribute('role', 'radio');
      card.setAttribute('aria-checked', String(c.id === settings.tandemRival));
      card.style.setProperty('--rival', c.colours.primary);
      card.style.setProperty('--rival-2', c.colours.secondary);

      const bust = document.createElement('div');
      bust.className = 'rival__bust';
      if (c.bust) {
        const img = document.createElement('img');
        img.src = c.bust;
        img.alt = '';
        bust.appendChild(img);
      } else {
        // No art yet: their initial, in their colours.
        bust.textContent = c.name.charAt(0);
      }
      const name = document.createElement('div');
      name.className = 'rival__name';
      name.textContent = c.name;
      const tier = document.createElement('div');
      tier.className = 'rival__tier';
      tier.textContent = c.tier;
      const pips = document.createElement('div');
      pips.className = 'rival__pips';
      pips.setAttribute('aria-label', `Difficulty ${c.level} of 4`);
      for (let i = 1; i <= 4; i++) {
        const pip = document.createElement('span');
        pip.className = i <= c.level ? 'rival__pip rival__pip--on' : 'rival__pip';
        pips.appendChild(pip);
      }
      card.append(bust, name, tier, pips);
      if (route && hasBeaten(route.id, c.id)) {
        const done = document.createElement('span');
        done.className = 'rival__beaten';
        done.textContent = '✓';
        done.title = 'Beaten on this route';
        card.appendChild(done);
      }
      card.addEventListener('click', () => {
        settings.tandemRival = c.id;
        saveSettings(settings);
        setBoardMode('tandem');
      });
      return card;
    }),
  );
}

function newHouseBattle(): Battle | null {
  if (!currentRoute) return null;
  const rival = characterById(settings.tandemRival);
  return { kind: 'house', rival, name: rival.name, skill: rival.skill, run: 1, omt: 0, youChase: 0, theyLead: 0, youLead: 0, theyChase: 0, timeMs: 0, runs: [], rounds: [] };
}

/** The caption for the run under way: "Run 1/2", "One more time · Run 2/2", "Chase". */
function battleRunLabel(b: Battle): string {
  if (b.kind === 'chase') return 'Chase';
  return `${b.omt > 0 ? 'OMT · ' : ''}Run ${b.run}/2`;
}

/** Start the battle's current run: in run 1 the player chases, in run 2 leads. */
function startBattleRun(restart = false): void {
  const route = currentRoute;
  const b = battle;
  if (!route || !b) return;
  const car = carById(settings.carId);
  let t: TandemState;
  if (b.kind === 'chase' && b.leader) {
    t = createTandem(route, 'chase', b.leader.car, { kind: 'replay', inputs: b.leader.inputs });
  } else if (b.run === 1) {
    t = createTandem(route, 'chase', car, { kind: 'leadBot', skill: b.skill });
  } else {
    t = createTandem(route, 'lead', car, { kind: 'chaseBot', skill: b.skill });
  }
  lastPageMode = 'tandem';
  void startRun('driftRun', restart, t);
}

function startHouseBattle(): void {
  battle = newHouseBattle();
  startBattleRun();
}

/** Chase a posted Drift Run: its recording leads. */
async function startChase(row: LeaderboardRow): Promise<void> {
  // The engine sound has to start inside this tap, before the download.
  audio?.stop();
  audio = new EngineAudio(settings.soundOn);
  void audio.start(carById(settings.carId).engine);
  try {
    const inputs = InputRecorder.decode(await decompress(await fetchReplay(row.id)));
    battle = {
      kind: 'chase',
      name: row.playerName,
      skill: 0,
      run: 1,
      omt: 0,
      runs: [],
      rounds: [],
      youChase: 0,
      theyLead: 0,
      youLead: 0,
      theyChase: 0,
      timeMs: 0,
      leader: { inputs, car: carById(row.carId) },
    };
    startBattleRun(true);
  } catch {
    $('chase-rows').prepend(caption(`Could not load ${row.playerName}'s run.`));
  }
}

/** A tandem run is over: record both drivers' scores and, after run 2, decide the battle. */
function finishBattleRun(outcome: RunOutcome): void {
  const b = battle;
  const t = outcome.tandem;
  if (!b || !t) return;
  let credits: Credit[] = [];
  const you = tandemScore(t.player);
  const them = tandemScore(t.opponent);
  b.timeMs += Math.round(outcome.result.timeSeconds * 1000);
  b.outcome = undefined;
  b.runs[b.kind === 'chase' ? 0 : b.run - 1] = { you: t.player, them: t.opponent };
  if (b.kind === 'chase' || b.run === 1) {
    b.youChase = you;
    b.theyLead = them;
  } else {
    b.youLead = you;
    b.theyChase = them;
    const yours = b.youChase + b.youLead;
    const theirs = b.theyLead + b.theyChase;
    // Within 2% is too close to call, as the judges would say: one more time.
    const close = Math.abs(yours - theirs) <= 0.02 * Math.max(yours, theirs, 1);
    b.outcome = close ? 'omt' : yours > theirs ? 'win' : 'loss';
    if (close) b.rounds.push([yours, theirs]);
    if (b.outcome === 'win' && b.rival && currentRoute) {
      markBeaten(currentRoute.id, b.rival.id);
      if (!tuneMode) credits = creditBattleWin(currentRoute.id, settings.carId, b.rival.skill);
    }
  }
  showBattleResults();
  showCredits(credits);
}

/**
 * The battle as a table: a column each for the player and the opponent, a row
 * per run, and the totals under them -- so the sums add up down the page and
 * the winner is plain. After the deciding run the winner's total is filled in
 * red; a tie-breaker outlines both, and says why.
 */
function battleTable(b: Battle, final: boolean): HTMLElement {
  const table = document.createElement('table');
  table.className = 'battle';
  const row = (...cells: HTMLElement[]) => {
    const tr = document.createElement('tr');
    tr.append(...cells);
    return tr;
  };
  const head = (text: string, className = '') => {
    const th = document.createElement('th');
    th.textContent = text;
    if (className) th.className = className;
    return th;
  };
  /** A score in its own box, so neighbouring totals never touch; ticked when it took the round. */
  const score = (value: number | null, mine: boolean, mark: '' | 'won' | 'win' | 'lose' | 'tie' = '') => {
    const td = document.createElement('td');
    if (mine) td.className = 'battle__you';
    const box = document.createElement('span');
    box.className = `battle__score${mark ? ` battle__score--${mark}` : ''}`;
    box.textContent = value === null ? '—' : String(value);
    if (mark === 'won') box.setAttribute('aria-label', `${value}, took this run`);
    td.appendChild(box);
    return td;
  };
  const runLabel = (title: string, detail: string) => {
    const th = document.createElement('th');
    th.scope = 'row';
    const t = document.createElement('span');
    t.className = 'battle__run';
    t.textContent = title;
    const d = document.createElement('span');
    d.className = 'battle__role';
    d.textContent = detail;
    th.append(t, d);
    return th;
  };
  // Who took a run: the higher score. A dead heat in a run ticks neither.
  const runRow = (title: string, detail: string, yours: number | null, theirs: number | null) =>
    row(
      runLabel(title, detail),
      score(yours, true, yours !== null && theirs !== null && yours > theirs ? 'won' : ''),
      score(theirs, false, yours !== null && theirs !== null && theirs > yours ? 'won' : ''),
    );

  const thead = document.createElement('thead');
  thead.append(row(head(''), head('You', 'battle__you'), head(b.name, 'battle__them')));
  const tbody = document.createElement('tbody');
  if (b.kind === 'chase') {
    tbody.append(runRow('Chase', `you chase · ${b.name} leads`, b.youChase, b.theyLead));
    table.append(thead, tbody);
    return table;
  }
  tbody.append(
    runRow('Run 1', `you chase · ${b.name} leads`, b.youChase, b.theyLead),
    runRow('Run 2', `you lead · ${b.name} chases`, final ? b.youLead : null, final ? b.theyChase : null),
  );
  table.append(thead, tbody);
  if (final) {
    const yours = b.youChase + b.youLead;
    const theirs = b.theyLead + b.theyChase;
    const mark = (won: boolean) => (b.outcome === 'omt' ? 'tie' : won ? 'win' : 'lose');
    const tfoot = document.createElement('tfoot');
    const total = head('Total');
    total.scope = 'row';
    tfoot.append(row(total, score(yours, true, mark(b.outcome === 'win')), score(theirs, false, mark(b.outcome === 'loss'))));
    if (b.outcome === 'omt') {
      // In the judges' words: too close to call, so they call One More Time.
      const note = document.createElement('td');
      note.colSpan = 3;
      note.className = 'battle__note';
      const lead = document.createElement('strong');
      lead.textContent = 'Too close to call: within 2% of each other.';
      note.append(
        lead,
        ` The judges call One More Time (OMT): both runs again, you chasing first, and the rerun settles it.`,
      );
      tfoot.append(row(note));
    }
    table.append(tfoot);
  }
  return table;
}

/** The judges' word on the battle so far: each run, who took it and why, and where One More Time stands. */
function judgesWord(b: Battle): HTMLElement {
  const box = document.createElement('div');
  box.className = 'judges';
  const label = document.createElement('p');
  label.className = 'judges__label';
  label.textContent = 'From the judges';
  box.appendChild(label);
  for (const line of battleVerdict(b)) {
    const p = document.createElement('p');
    p.className = 'judges__line';
    p.textContent = line;
    box.appendChild(p);
  }
  return box;
}

function showBattleResults(): void {
  const b = battle;
  const route = currentRoute;
  if (!b || !route) return;
  const car = carById(settings.carId);
  const final = b.kind === 'house' && b.run === 2;

  $('result-title').textContent =
    b.kind === 'chase'
      ? 'Chase done'
      : !final
        ? 'Run 1 done'
        : b.outcome === 'win'
          ? 'You win'
          : b.outcome === 'loss'
            ? `${b.name} wins`
            : 'One more time';
  $('result-route').textContent = `${currentEntry.name} · ${car.name} · vs ${b.name}`;
  const portrait = $<HTMLImageElement>('result-rival');
  portrait.hidden = !b.rival?.bust;
  if (b.rival?.bust) portrait.src = b.rival.bust;

  $('result-stats').replaceChildren(battleTable(b, final), judgesWord(b));

  // What next: the second run, one more time, or another battle.
  $('btn-retry').textContent =
    b.kind === 'chase' ? 'Chase again' : !final ? 'Run 2: you lead' : b.outcome === 'omt' ? 'One more time' : 'Battle again';
  // Changing car mid-battle would start a new one: only offered between battles.
  $('btn-result-car-change').hidden = b.kind === 'house' && (!final || b.outcome === 'omt');

  // Only a decided battle against the house is ranked.
  const ranked = final && b.outcome !== 'omt' && !tuneMode;
  resetSubmitPanel();
  const note =
    b.kind === 'chase'
      ? "Chasing a posted run isn't ranked: only battles against the house are."
      : tuneMode && final
        ? 'Tuning mode. This battle is not posted to the leaderboard.'
        : '';
  $('result-tune-note').textContent = note;
  $('result-tune-note').hidden = !note;
  $('result-submit').hidden = !ranked;
  showScreen('results');
  if (ranked) {
    resultCar = 'category';
    void showResultBoard();
  }
}

/** The results screen's primary button: the next step of a battle, or the same run again. */
function retry(): void {
  const b = battle;
  if (!b) {
    void startRun(currentMode);
    return;
  }
  if (b.kind === 'chase') {
    startBattleRun();
    return;
  }
  if (b.run === 1) {
    b.run = 2;
    startBattleRun();
    return;
  }
  if (b.outcome === 'omt') {
    b.omt++;
    b.run = 1;
    b.runs = [];
    b.timeMs = 0;
    b.outcome = undefined;
    startBattleRun();
    return;
  }
  startHouseBattle();
}

/** The board the results show: a house battle's, or the run's own. */
function resultBoardKey(): ApiMode {
  return battle?.kind === 'house' && battle.rival ? rivalBoard(battle.rival) : boardFor(currentMode, currentControls);
}

// --- Ghosts -----------------------------------------------------------------

function toggleGhost(row: LeaderboardRow, board: ApiMode): void {
  const route = currentRoute;
  if (!route) return;
  if (ghostPick?.row.id === row.id) {
    setGhostPick(null);
    return;
  }
  const input = fetchReplay(row.id).then((gz) => decompress(gz));
  const pick: GhostPick = { row, board, routeId: route.id, routeVersion: route.version, input };
  // The ghost's car art, so it is there when the race starts.
  void preload([carById(row.carId).sprite?.path]);
  input.catch(() => {
    if (ghostPick !== pick) return;
    setGhostPick(null);
    showGhostNote(`Could not load ${row.playerName}'s ghost.`);
  });
  setGhostPick(pick);
}

function setGhostPick(pick: GhostPick | null): void {
  ghostPick = pick;
  for (const button of document.querySelectorAll<HTMLElement>('.board__ghost')) {
    button.setAttribute('aria-pressed', String(!!pick && button.dataset.ghostId === pick.row.id));
  }
  if (pick) {
    const r = pick.row;
    const value = pick.board === 'driftRun' ? r.points.toLocaleString('en-GB') : formatTime(r.timeMs / 1000);
    showGhostNote(`Racing ${r.playerName}'s ghost · ${carById(r.carId).name} · ${value}`, true);
  } else {
    showGhostNote('');
  }
}

/** The line under the mode buttons saying which ghost Go will race. */
function showGhostNote(text: string, removable = false): void {
  $('ghost-note-text').textContent = text;
  $('btn-ghost-clear').hidden = !removable;
  $('ghost-note').hidden = text === '';
}

/** The picked ghost, driven in full, if it belongs to this run's route and board. */
async function loadGhostFor(route: RouteData, board: ApiMode, mode: SimMode): Promise<GhostTrack | null> {
  const pick = ghostPick;
  if (!pick || pick.board !== board || pick.routeId !== route.id || pick.routeVersion !== route.version) return null;
  try {
    const bytes = await pick.input;
    // The controls the ghost was driven with: only throttle Drift Runs are
    // posted, and Time Attack's board says which level.
    const controls: Controls = board === 'driftRun' ? 'throttle' : board === 'timeAttackPro' ? 'pedals' : 'steer';
    return buildGhost(pick.row.playerName, carById(pick.row.carId), route, configFor(mode, controls), bytes);
  } catch {
    return null;
  }
}

function caption(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'caption';
  el.textContent = text;
  return el;
}

function statRow(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'row';
  const l = document.createElement('span');
  l.textContent = label;
  const v = document.createElement('span');
  v.className = 'row__value';
  v.textContent = value;
  row.append(l, v);
  return row;
}

/**
 * Course map.
 *
 * Drawn from the same centreline the sim drives on, so it cannot disagree with
 * the road. When a stylised poster image is supplied for a route it replaces
 * this; until then the player still gets an accurate read of the corner
 * sequence, which is the part that actually matters before a first run.
 */
/**
 * Route intro artwork.
 *
 * Uses the supplied poster when one has loaded, and the drawn course map
 * otherwise. Both answer the same question -- what shape is this route -- and
 * the drawn one is generated from the centreline the sim drives, so it can
 * never disagree with the road even when the poster does.
 */
function showPoster(route: RouteData): void {
  const art = currentEntry.poster;
  const poster = getSprite(art?.src);
  const frame = $('poster-art');
  const canvas = $<HTMLCanvasElement>('minimap');
  if (art && poster) {
    $<HTMLImageElement>('poster-img').src = poster.src;
    drawPosterOverlay($('poster-overlay') as unknown as SVGSVGElement, art);
    frame.hidden = false;
    canvas.hidden = true;
    return;
  }
  frame.hidden = true;
  canvas.hidden = false;
  drawMinimap(route);
}

function drawMinimap(route: RouteData): void {
  const el = $<HTMLCanvasElement>('minimap');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = el.clientWidth || 480;
  const height = Math.round(width * 0.62);
  el.width = Math.round(width * dpr);
  el.height = Math.round(height * dpr);
  el.style.height = `${height}px`;

  const ctx = el.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const s = route.samples;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < s.x.length; i++) {
    if (s.x[i] < minX) minX = s.x[i];
    if (s.x[i] > maxX) maxX = s.x[i];
    if (s.y[i] < minY) minY = s.y[i];
    if (s.y[i] > maxY) maxY = s.y[i];
  }

  const pad = 18;
  const scale = Math.min((width - pad * 2) / (maxX - minX || 1), (height - pad * 2) / (maxY - minY || 1));
  const ox = (width - (maxX - minX) * scale) / 2 - minX * scale;
  // Flip y: world y is up, canvas y is down.
  const oy = (height + (maxY - minY) * scale) / 2 + minY * scale;
  const px = (i: number) => s.x[i] * scale + ox;
  const py = (i: number) => -s.y[i] * scale + oy;

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  ctx.strokeStyle = '#141414';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(px(0), py(0));
  for (let i = 1; i < s.x.length; i++) ctx.lineTo(px(i), py(i));
  ctx.stroke();

  // Clipping points, not whole drift zones.
  //
  // Drawing the zones as red overlay swallowed most of the route -- on a touge
  // nearly every corner is a scoring zone -- and a map that is 70% red breaks
  // the one rule the design system is most insistent about. The clip points
  // carry the same information a player actually needs from a course map:
  // where the apexes are and how they are strung together.
  //
  // On a long route there are hundreds of them, and at map scale they merge
  // into a red snake thicker than the road -- the imported 7km pass drew more
  // red than ink. So they shrink, and any that would overlap the last one
  // drawn are skipped: the shape of the route has to stay readable.
  ctx.fillStyle = '#e8402a';
  const size = route.clipPoints.length > 40 ? 3 : 5;
  let lastX = -100;
  let lastY = -100;
  for (const clip of route.clipPoints) {
    const cx = px(clip.index);
    const cy = py(clip.index);
    if (Math.hypot(cx - lastX, cy - lastY) < size * 2) continue;
    ctx.fillRect(cx - size / 2, cy - size / 2, size, size);
    lastX = cx;
    lastY = cy;
  }

  // Start and finish.
  const last = s.x.length - 1;
  ctx.fillStyle = '#141414';
  ctx.fillRect(px(0) - 4, py(0) - 4, 8, 8);
  ctx.fillStyle = '#e8402a';
  ctx.fillRect(px(last) - 4, py(last) - 4, 8, 8);

  ctx.fillStyle = '#6d675c';
  ctx.font = '600 10px "IBM Plex Mono", monospace';
  ctx.fillText('START', px(0) + 8, py(0) + 4);
  ctx.fillText('FINISH', px(last) + 8, py(last) + 4);
}

// --- Garage -----------------------------------------------------------------

/**
 * Where the garage goes back to. From a route's page it is a detour: picking a
 * car returns to that route rather than to the main menu. From the results it
 * is a rematch: picking a car runs the same route and mode again in it.
 */
let garageReturn: 'title' | 'intro' | 'rerun' = 'title';

function openGarage(from: 'title' | 'intro' | 'rerun'): void {
  garageReturn = from;
  garageDetails = null;
  buildCarList();
  showScreen('garage');
  void loadGarageStats();
}

function leaveGarage(): void {
  if (garageReturn === 'rerun') {
    showScreen('results');
    return;
  }
  if (garageReturn === 'intro') {
    showIntroCar();
    setBoardMode(boardMode);
    showScreen('intro');
  } else {
    showScreen('title');
  }
}

/** The selected car on a route's page: its hero image (or top-down drawing) and name. */
function showIntroCar(): void {
  const car = carById(settings.carId);
  $('intro-car-name').textContent = car.name;
  const img = $<HTMLImageElement>('intro-car-sprite');
  const src = car.hero ?? car.sprite?.path;
  img.hidden = !src;
  if (src) img.src = src;
  // The top-down drawing is laid on its side to fit; the hero is already wide.
  img.classList.toggle('intro-car__img--sprite', !car.hero);
  img.classList.toggle('intro-car__img--zoom', !!car.hero);
  showIntroChallenge();
}

// --- Route poster, zoomed ---------------------------------------------------

/**
 * The route's painting full screen, at a size a phone can actually read: as
 * tall as the screen and as wide as that makes it, to drag across. The full
 * resolution copy loads only here.
 */
function openPosterZoom(): void {
  const art = currentEntry.poster;
  if (!art) return;
  const box = $('poster-lightbox');
  const img = $<HTMLImageElement>('lightbox-img');
  drawPosterOverlay($('lightbox-overlay') as unknown as SVGSVGElement, art);
  // Start on the smaller copy, already loaded, and swap in the sharp one.
  img.src = art.src;
  const sharp = new Image();
  sharp.onload = () => {
    if (!box.hidden) img.src = art.full;
  };
  sharp.src = art.full;
  box.hidden = false;
  // Centre the view on the painting.
  requestAnimationFrame(() => {
    const scroller = $('lightbox-scroll');
    scroller.scrollLeft = (scroller.scrollWidth - scroller.clientWidth) / 2;
    scroller.scrollTop = (scroller.scrollHeight - scroller.clientHeight) / 2;
  });
}

function closePosterZoom(): void {
  $('poster-lightbox').hidden = true;
  $('poster-lightbox').classList.remove('lightbox--car');
}

/**
 * A car's hero image full screen, like a route's painting: as tall as the
 * screen, dragged across on a phone. The full-size copy loads only here.
 */
function openCarZoom(car: (typeof CARS)[number]): void {
  if (!car.hero) return;
  const box = $('poster-lightbox');
  const img = $<HTMLImageElement>('lightbox-img');
  // No route marks on a car.
  $('lightbox-overlay').replaceChildren();
  box.classList.add('lightbox--car');
  img.src = car.hero;
  if (car.heroFull) {
    const full = car.heroFull;
    const sharp = new Image();
    sharp.onload = () => {
      if (!box.hidden) img.src = full;
    };
    sharp.src = full;
  }
  box.hidden = false;
  requestAnimationFrame(() => {
    const scroller = $('lightbox-scroll');
    scroller.scrollLeft = (scroller.scrollWidth - scroller.clientWidth) / 2;
    scroller.scrollTop = (scroller.scrollHeight - scroller.clientHeight) / 2;
  });
}

/** How the garage is laid out: kept while the game is open. */
let garageSort: GarageSort = 'category';
let garageFilter: GarageFilter = 'all';
/** What the boards say about each car, once they have answered. */
let garageStats: GarageResponse | null = null;
/** The car whose details are open, if any. */
let garageDetails: string | null = null;

const SORT_NOTES: Record<GarageSort, string> = {
  category: 'Fastest first in each category, by measured lap time.',
  accel: 'Quickest first: the real car’s 0-100km/h.',
  speed: 'Fastest first: the real car’s top speed, km/h.',
  drift: 'Most points first: what TAKA scores in each car, the best car 100.',
  boards: 'Most places in the top 10s first, over every course: Drift Run · Time Attack.',
};

/** Ask the boards about every car, then lay the garage out again with what they say. */
async function loadGarageStats(): Promise<void> {
  const routes = listedRoutes()
    .map((r) => ({ id: r.id, version: TARGETS.routes[r.id]?.version }))
    .filter((r): r is { id: string; version: number } => r.version !== undefined);
  try {
    garageStats = await fetchGarage(SIM_VERSION, routes, settings.playerName);
    buildCarList();
  } catch {
    // The garage works without it: the top-10 counts stay as dashes.
  }
}

/** Courses where this device's best was set in this car, this sim version. */
function bestsIn(carId: string): number {
  return new Set(allBests().filter((b) => b.carId === carId && b.simVersion === SIM_VERSION).map((b) => b.routeId)).size;
}

function buildCarList(): void {
  for (const b of $('garage-filter').querySelectorAll<HTMLElement>('[data-filter]')) {
    b.setAttribute('aria-pressed', String(b.dataset.filter === garageFilter));
  }
  for (const b of $('garage-sort').querySelectorAll<HTMLElement>('[data-sort]')) {
    b.setAttribute('aria-pressed', String(b.dataset.sort === garageSort));
  }
  $('garage-sort-note').textContent = SORT_NOTES[garageSort];

  const out: HTMLElement[] = [];
  for (const group of orderCars(garageSort, garageFilter, garageStats?.boards ?? null)) {
    if (group.category) {
      const head = document.createElement('div');
      head.className = 'garage-section';
      const title = document.createElement('h3');
      title.className = 'title';
      title.textContent = group.category.name;
      const line = document.createElement('div');
      line.className = 'caption';
      line.textContent = CATEGORY_LINES[group.category.id];
      head.append(title, line);
      out.push(head);
    }
    group.cars.forEach((car, i) => out.push(carRow(car, group.category ? null : i + 1)));
  }
  $('car-list').replaceChildren(...out);
}

/**
 * One car in the garage: a slim row to pick it by -- picture, name, the three
 * measures, and the player's own record in it -- with its details folded
 * away underneath.
 */
function carRow(car: (typeof CARS)[number], rank: number | null): HTMLElement {
  const lock = lockOf(car.id);
  const selected = car.id === settings.carId;
  const category = categoryOf(car.id);
  const row = document.createElement('div');
  row.className = 'car-row';
  row.classList.toggle('car-row--selected', selected);
  row.classList.toggle('car-row--locked', !!lock);

  const main = document.createElement('button');
  main.type = 'button';
  main.className = 'car-row__main';
  if (lock) main.setAttribute('aria-disabled', 'true');

  // The whole drawing, as it was made; its place on its corner when the list
  // is in a measured order.
  const hero = carHero(car);
  if (rank !== null) {
    const n = document.createElement('span');
    n.className = 'car-row__rank';
    n.textContent = String(rank);
    hero.appendChild(n);
  }
  main.appendChild(hero);

  const text = document.createElement('div');
  text.className = 'car-row__text';
  const name = document.createElement('div');
  name.className = 'card__name';
  name.textContent = car.name;
  if (category) {
    const tag = document.createElement('span');
    tag.className = 'car-card__category';
    tag.textContent = category.name;
    name.appendChild(tag);
  }
  const kpis = document.createElement('div');
  kpis.className = 'car-row__kpis';
  const kpi = (label: string, value: string, sorted: boolean) => {
    const cell = document.createElement('span');
    cell.className = sorted ? 'car-row__kpi car-row__kpi--sorted' : 'car-row__kpi';
    const l = document.createElement('span');
    l.className = 'car-stats__label';
    l.textContent = label;
    const v = document.createElement('b');
    v.textContent = value;
    cell.append(l, v);
    return cell;
  };
  kpis.append(
    kpi('0-100', formatZeroTo100(car), garageSort === 'accel'),
    kpi('Top km/h', formatTopSpeed(car), garageSort === 'speed'),
    kpi('Drift', formatDrift(car.id), garageSort === 'drift'),
    kpi('Top 10s', formatTops(car.id, garageStats?.boards ?? null), garageSort === 'boards'),
  );
  // What it is, in the garage's words: the engine, then the car's character.
  const meta = document.createElement('div');
  meta.className = 'card__meta';
  meta.textContent = PROFILES[car.engine].label + (tuneMode && isTuned(car) ? ' · tuned' : '');
  text.append(name, meta, kpis);
  if (car.tagline) {
    const line = document.createElement('div');
    line.className = 'car-card__tagline';
    line.textContent = car.tagline;
    text.appendChild(line);
  }

  // The player's own line: what opens a locked car, or their record in an open one.
  const you = document.createElement('div');
  you.className = lock ? 'car-row__you car-row__you--lock' : 'car-row__you';
  if (lock) {
    you.textContent = `Locked · ${lock.done}/${lock.challenge.courses} · ${describeChallenge(lock.challenge, category?.name ?? '')}`;
  } else {
    const place = placeLine(car.id, garageStats?.mine ?? null);
    const bests = bestsIn(car.id);
    const parts = [
      place ? `Best ${place}` : null,
      bests > 0 ? `your best on ${bests} course${bests === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    you.textContent = parts.length > 0 ? `You: ${parts.join(' · ')}` : '';
  }
  if (you.textContent) text.appendChild(you);
  main.appendChild(text);

  // The selected car offers the next step instead: tapping it again goes to
  // the routes -- or, when the garage was opened from a route, back to it.
  if (selected) {
    const go = document.createElement('span');
    go.className = 'go-race car-row__go';
    go.textContent = garageReturn === 'intro' ? 'Use this car' : garageReturn === 'rerun' ? 'Race this car' : 'Go race';
    main.appendChild(go);
  }
  main.addEventListener('click', () => pickCar(car.id, !!lock, selected));

  const open = garageDetails === car.id;
  const more = document.createElement('button');
  more.type = 'button';
  more.className = 'car-row__more';
  more.textContent = open ? 'Hide details' : 'Details';
  more.setAttribute('aria-expanded', String(open));
  more.addEventListener('click', () => {
    garageDetails = open ? null : car.id;
    buildCarList();
  });

  row.append(main, more);
  if (open) row.appendChild(carDetails(car));
  return row;
}

/** A car's details, unfolded: its character as six rated scales. */
function carDetails(car: (typeof CARS)[number]): HTMLElement {
  const box = document.createElement('div');
  box.className = 'car-row__details';
  if (car.stats) box.appendChild(carStats(car.stats));
  return box;
}

function pickCar(carId: string, locked: boolean, selected: boolean): void {
  if (locked) {
    // Not yet: its details say what opens it.
    garageDetails = carId;
    buildCarList();
    return;
  }
  if (!selected) {
    settings.carId = carId;
    saveSettings(settings);
  }
  if (garageReturn === 'rerun') {
    // Picked after a run: the same route and mode again, in this car.
    // Started from this tap, which is what lets the engine sound start.
    // After a battle, that is a new battle, the house in the new car too.
    if (battle?.kind === 'house') startHouseBattle();
    else if (battle) startBattleRun();
    else void startRun(currentMode);
    return;
  }
  if (garageReturn === 'intro') {
    // Picked from a route's page: straight back to it.
    leaveGarage();
    return;
  }
  if (selected) {
    buildRouteList();
    showScreen('routes');
    return;
  }
  buildCarList();
}

/** The car's character, as six rows of 1-5 pips. */
function carStats(stats: NonNullable<(typeof CARS)[number]['stats']>): HTMLElement {
  const el = document.createElement('div');
  el.className = 'car-stats';
  const rows: [string, number][] = [
    ['Acceleration', stats.accel],
    ['Top speed', stats.topSpeed],
    ['Grip', stats.grip],
    ['Drift', stats.drift],
    ['Agility', stats.agility],
    ['Stability', stats.stability],
  ];
  for (const [label, value] of rows) {
    const name = document.createElement('span');
    name.className = 'car-stats__label';
    name.textContent = label;
    const pips = document.createElement('span');
    pips.className = 'car-stats__pips';
    pips.setAttribute('aria-label', `${value} of 5`);
    for (let i = 1; i <= 5; i++) {
      const pip = document.createElement('span');
      pip.className = i <= value ? 'car-stats__pip car-stats__pip--on' : 'car-stats__pip';
      pips.appendChild(pip);
    }
    el.append(name, pips);
  }
  return el;
}

/**
 * A car's hero image, or a placeholder in its place: the top-down drawing (if
 * there is one) laid on its side over a hatched panel, in the car's colour.
 */
function carHero(car: (typeof CARS)[number]): HTMLElement {
  const hero = document.createElement('div');
  hero.className = 'car-hero';
  if (car.hero) {
    const img = document.createElement('img');
    img.src = car.hero;
    img.alt = car.name;
    img.decoding = 'async';
    img.loading = 'lazy';
    hero.appendChild(img);
    const zoom = document.createElement('span');
    zoom.className = 'poster__zoom car-hero__zoom';
    zoom.setAttribute('role', 'button');
    zoom.setAttribute('aria-label', `Zoom into the ${car.name}`);
    zoom.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" /></svg>';
    zoom.addEventListener('click', (e) => {
      // Inside the card's button: zoom, and do not pick the car as well.
      e.stopPropagation();
      openCarZoom(car);
    });
    hero.appendChild(zoom);
    return hero;
  }
  hero.classList.add('car-hero--placeholder');
  hero.style.setProperty('--car-tint', car.tint ?? '#e8402a');
  if (car.sprite) {
    const img = document.createElement('img');
    img.className = 'car-hero__sprite';
    img.src = car.sprite.path;
    img.alt = '';
    hero.appendChild(img);
  } else {
    const shape = document.createElement('div');
    shape.className = 'car-hero__shape';
    hero.appendChild(shape);
  }
  const label = document.createElement('div');
  label.className = 'mono car-hero__label';
  label.textContent = 'HERO ART TO COME';
  hero.appendChild(label);
  return hero;
}

// --- Settings ---------------------------------------------------------------

function bindToggle(id: string, get: () => boolean, set: (value: boolean) => void): void {
  const el = $(id);
  const sync = () => el.setAttribute('aria-pressed', String(get()));
  sync();
  el.addEventListener('click', () => {
    set(!get());
    saveSettings(settings);
    sync();
  });
}

let syncSettingsSensitivity: () => void = () => {};
let syncTuneToggle: () => void = () => {};

function setSensitivity(value: number): void {
  settings.steerSensitivity = value;
  thumb.setSensitivity(value);
  saveSettings(settings);
  syncSettingsSensitivity();
}

function buildSettings(): void {
  const sensitivity = $<HTMLInputElement>('set-sensitivity');
  const sensitivityValue = $('sensitivity-value');
  const syncSensitivity = () => {
    sensitivity.value = String(Math.round(settings.steerSensitivity * 100));
    sensitivityValue.textContent = `${sensitivity.value}%`;
  };
  syncSensitivity();
  sensitivity.addEventListener('input', () => setSensitivity(Number(sensitivity.value) / 100));
  syncSettingsSensitivity = syncSensitivity;

  bindToggle('set-north', () => settings.fixedNorth, (v) => (settings.fixedNorth = v));
  bindToggle('set-skids', () => settings.showSkidMarks, (v) => (settings.showSkidMarks = v));
  bindToggle('set-smoke', () => settings.showSmoke, (v) => (settings.showSmoke = v));
  bindToggle('set-sound', () => settings.soundOn, (v) => (settings.soundOn = v));
  const opponent = $<HTMLInputElement>('set-opponent-volume');
  const syncOpponent = () => {
    opponent.value = String(Math.round(settings.opponentVolume * 100));
    $('opponent-volume-value').textContent = `${opponent.value}%`;
  };
  syncOpponent();
  opponent.addEventListener('input', () => {
    settings.opponentVolume = Number(opponent.value) / 100;
    saveSettings(settings);
    syncOpponent();
    partnerAudio?.setLevel(settings.opponentVolume);
  });
  bindToggle(
    'set-drift-steer',
    () => settings.driftControls === 'steer',
    (v) => (settings.driftControls = v ? 'steer' : 'throttle'),
  );
  bindToggle('set-tune', () => tuneMode, (v) => {
    // Only reachable from Settings, never mid-run, so a run is driven entirely
    // on shipped or entirely on tuned handling.
    setTuneMode(v);
    buildCarList();
  });
  syncTuneToggle = () => $('set-tune').setAttribute('aria-pressed', String(tuneMode));

  buildKeybinds();
  $('btn-reset-keys').addEventListener('click', () => {
    settings.keymap = structuredClone(DEFAULT_KEYMAP);
    input.setKeymap(settings.keymap);
    saveSettings(settings);
    buildKeybinds();
    syncKeyHints();
  });
}

/**
 * Rebinding: click a row, press a key.
 *
 * Captures on keydown at the window level while listening, so a bind can target
 * any key including ones the browser would otherwise act on.
 */
let listeningFor: Action | null = null;

function buildKeybinds(): void {
  $('keybinds').replaceChildren(
    ...ACTIONS.map(({ id, label }) => {
      const row = document.createElement('div');
      row.className = 'row';
      row.style.cursor = 'pointer';

      const name = document.createElement('span');
      name.textContent = label;

      const value = document.createElement('span');
      value.className = 'row__value';
      value.dataset.action = id;
      value.textContent = settings.keymap[id].map(keyLabel).join(' / ');

      row.append(name, value);
      row.addEventListener('click', () => {
        listeningFor = id;
        value.textContent = 'PRESS A KEY';
        value.style.color = 'var(--red)';
      });
      return row;
    }),
  );
}

window.addEventListener(
  'keydown',
  (e) => {
    if (!listeningFor) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.code !== 'Escape') {
      settings.keymap[listeningFor] = [e.code];
      input.setKeymap(settings.keymap);
      saveSettings(settings);
    }
    listeningFor = null;
    buildKeybinds();
    syncKeyHints();
  },
  true,
);

function syncKeyHints(): void {
  const map: [string, Action][] = [
    ['hint-left', 'left'],
    ['hint-right', 'right'],
    ['hint-throttle', 'throttle'],
    ['hint-gas', 'throttle'],
    ['hint-brake', 'brake'],
    ['hint-drift', 'drift'],
  ];
  for (const [id, action] of map) {
    $(id).textContent = keyLabel(settings.keymap[action][0]);
  }
}

// --- Running ----------------------------------------------------------------

let audio: EngineAudio | null = null;
/** The other tandem car's engine, sharing the player's audio. */
let partnerAudio: EngineAudio | null = null;

/**
 * Start a run. A restart keeps the engine sound of the run it replaces: it may
 * come from holding the pause button, which is not a tap, and a phone will not
 * start new sound without one.
 */
async function startRun(mode: SimMode, restart = false, tandem: TandemState | null = null): Promise<void> {
  if (!currentRoute) return;
  currentMode = mode;
  if (!tandem) {
    battle = null;
    lastPageMode = mode;
  }

  const car = carById(settings.carId);
  // Tandem is Drift Run on the throttle controls, whatever the setting.
  currentControls = tandem
    ? 'throttle'
    : mode === 'driftRun'
      ? settings.driftControls
      : settings.timeAttackLevel === 'pro'
        ? 'pedals'
        : 'steer';
  gameEl.dataset.controls = currentControls;
  gameEl.dataset.mode = currentMode;
  const renderSettings: RenderSettings = {
    fixedNorth: settings.fixedNorth,
    showSkidMarks: settings.showSkidMarks,
    showSmoke: settings.showSmoke,
  };

  syncKeyHints();

  if (!restart || !audio) audio = new EngineAudio(settings.soundOn);
  // Started from the click that got us here, which is the gesture the browser
  // requires before an AudioContext will run.
  void audio.start(car.engine);

  // The other tandem car's engine: through the same, already unlocked, audio,
  // at the player's chosen level and muffled, as heard from outside it.
  partnerAudio?.stop();
  partnerAudio = null;
  if (tandem && settings.soundOn && settings.opponentVolume > 0 && audio.context) {
    partnerAudio = new EngineAudio(true, { level: settings.opponentVolume, muffle: true });
    void partnerAudio.start(tandem.partnerCar.engine, audio.context);
  }

  // After the audio: that has to start inside the tap on Go, before any wait.
  const ghostTrack = tandem ? null : await loadGhostFor(currentRoute, boardFor(mode, currentControls), mode);
  runGhost = ghostTrack && ghostPick ? { row: ghostPick.row, track: ghostTrack } : null;
  if (ghostPick && !ghostTrack) showGhostNote(`${ghostPick.row.playerName}'s ghost could not be replayed.`);
  hud.setGhost(ghostTrack);
  hud.setTandem(
    tandem && battle
      ? { playerRole: tandem.playerRole, name: battle.name, run: battleRunLabel(battle), colour: battle.rival?.accent }
      : null,
  );

  session = new GameSession(
    currentRoute,
    car,
    configFor(mode, currentControls),
    input,
    renderer,
    hud,
    audio,
    renderSettings,
    ghostTrack,
    tandem,
    partnerAudio,
    battle?.rival ? battle.rival.colours : null,
  );

  const countdownOverlay = $('overlay-countdown');
  const countdownEl = $('countdown');
  $('countdown-mode').textContent =
    tandem && battle
      ? `DRIFT TANDEM · ${battleRunLabel(battle).toUpperCase()}`
      : mode === 'driftRun'
        ? 'DRIFT RUN'
        : currentControls === 'pedals'
          ? 'TIME ATTACK · PRO'
          : 'TIME ATTACK';
  const keys = gameEl.dataset.input === 'keyboard';
  const key = (a: Action) => keyLabel(settings.keymap[a][0]);
  $('countdown-hint').textContent =
    currentControls === 'throttle'
      ? keys
        ? `Hold ${key('throttle')} for throttle. Press ${key('drift')} before a corner to drift: a tap is a soft kick, a short press a full one, too long and you spin. Then balance the angle with the throttle.`
        : 'Hold the right side for throttle. Press the left side before a corner to drift: a tap is a soft kick, a short press a full one, too long and you spin. Then balance the angle with the throttle.'
      : currentControls === 'pedals'
        ? keys
          ? `${key('left')} ${key('right')} to steer, ${key('throttle')} for gas, ${key('brake')} to brake. No help with speed: brake for the corners yourself.`
          : 'Slide on the left to steer. On the right: brake on the inner side, gas on the outer. No help with speed: brake for the corners yourself.'
        : keys
          ? `${key('left')} ${key('right')} to steer. The car drives itself.`
          : 'Touch anywhere and slide to steer. The car drives itself.';
  // The opponent's face, for the moment before a battle run.
  const rivalImg = $<HTMLImageElement>('countdown-rival');
  rivalImg.hidden = !(tandem && battle?.rival?.bust);
  if (tandem && battle?.rival?.bust) rivalImg.src = battle.rival.bust;
  if (tandem && battle) {
    const controls = keys
      ? `${key('throttle')} throttle, ${key('drift')} drift.`
      : 'Right side: throttle. Left side: drift.';
    $('countdown-hint').textContent =
      tandem.playerRole === 'chase'
        ? `You chase ${battle.name}. The game steers you onto the line: keep the gap bar green with throttle and angle, same side, same angle. ${controls}`
        : `You lead; ${battle.name} chases. Big angle, good speed: set the pace. ${controls}`;
  }
  countdownOverlay.hidden = false;

  session.onCountdown = (value) => {
    if (value <= 0) {
      countdownOverlay.hidden = true;
      return;
    }
    const n = Math.ceil(value);
    countdownEl.textContent = n <= 0 ? 'GO' : String(n);
    countdownEl.classList.toggle('overlay__count--go', n <= 0);
  };

  session.onFinish = (outcome) => void finishRun(outcome);

  showGame();
  session.start();
}

async function finishRun(outcome: RunOutcome): Promise<void> {
  thumb.releaseAll();
  pedals.releaseAll();
  proPedals.releaseAll();
  tunePanel.close();
  lastOutcome = outcome;
  if (outcome.tandem && battle) {
    finishBattleRun(outcome);
    return;
  }
  const route = currentRoute;
  if (!route) return;

  const car = carById(settings.carId);
  const { result } = outcome;

  // Tuned handling is not the game everyone else is playing, so nothing driven
  // with it counts: no best, no replay, no unlock, no leaderboard. Nor does a
  // Drift Run steered the old way, which is kept for comparison only.
  if (unrankedReason()) {
    showResults(result, false);
    showCredits([]);
    return;
  }

  markCompleted(route.id);

  const record = {
    routeId: route.id,
    routeVersion: route.version,
    mode: boardFor(currentMode, currentControls),
    carId: car.id,
    simVersion: SIM_VERSION,
    timeSeconds: result.timeSeconds,
    points: result.points,
    grade: result.grade,
    recordedAt: Date.now(),
  };
  const isBest = submitBest(record);
  const credits = creditRun(record);

  // Store the replay for the personal best only. Everything needed to replay it
  // exactly -- sim version, route version, car, seed -- is in the best
  // record above; this is just the input stream.
  if (isBest) {
    try {
      const gz = await compress(outcome.recorder.encode());
      await saveReplay(bestKey(route.id, route.version, boardFor(currentMode, currentControls)), gz);
    } catch {
      // Replay not stored. The score still stands.
    }
  }

  showResults(result, isBest);
  showCredits(credits);
}

/**
 * Under a run's results: a course just done for an unlock challenge, or the
 * car it opened. Nothing when the run did neither.
 */
function showCredits(credits: Credit[]): void {
  const el = $('result-unlock');
  el.replaceChildren(
    ...credits.map((c) => {
      const line = document.createElement('div');
      const name = carById(c.carId).name;
      if (c.unlocked) {
        line.className = 'unlock-note__open';
        line.textContent = `${name} unlocked. It’s in your garage.`;
      } else {
        line.textContent = `${name} challenge: this course counts. ${c.done}/${c.needed} done.`;
      }
      return line;
    }),
  );
  el.hidden = credits.length === 0;
  if (credits.some((c) => c.unlocked)) buildCarList();
}

/** What the selected car can do here, in the mode picked, towards an unlock challenge -- or null. */
function challengeLine(route: RouteData, mode: PageMode, carId: string): string | null {
  for (const challenge of CHALLENGES) {
    const lock = lockOf(challenge.carId);
    if (!lock || !challengeCars(challenge).includes(carId)) continue;
    const kindMode: PageMode = challenge.kind === 'drift' ? 'driftRun' : challenge.kind === 'time' ? 'timeAttack' : 'tandem';
    if (kindMode !== mode) continue;
    const name = carById(challenge.carId).name;
    const progress = `${lock.done}/${challenge.courses} done`;
    if (lock.routes.includes(route.id)) return `${name} challenge: done on this course. ${progress}.`;
    if (challenge.kind === 'tandem') {
      const rival = challenge.rival ? characterById(challenge.rival).name : 'the house';
      return `Unlock the ${name}: beat ${rival} here. ${progress}.`;
    }
    const target = targetFor(challenge.kind, route.id, route.version, carId);
    if (target === undefined) continue;
    const what = challenge.kind === 'drift' ? `score ${target.toLocaleString('en-US')}` : `beat ${formatTime(target)}`;
    return `Unlock the ${name}: ${what} here. ${progress}.`;
  }
  return null;
}

function showIntroChallenge(): void {
  const line = currentRoute ? challengeLine(currentRoute, boardMode, settings.carId) : null;
  $('intro-challenge').textContent = line ?? '';
  $('intro-challenge').hidden = line === null;
}

function showResults(result: RunOutcome['result'], isBest: boolean): void {
  const route = currentRoute;
  if (!route) return;

  $('result-title').textContent = isBest ? 'Best yet' : 'Run over';
  $('result-rival').hidden = true;
  $('btn-retry').textContent = 'Run again';
  $('btn-result-car-change').hidden = false;
  $('result-route').textContent = `${currentEntry.name} · ${currentEntry.location} · ${carById(settings.carId).name}`;
  const rows: HTMLElement[] = [statRow('Time', formatTime(result.timeSeconds))];

  if (currentMode === 'driftRun') {
    // The score as a sum the player can follow: what drifting earned, what the
    // run's record added and took away, and the total.
    rows.push(
      scoreRow('Points earned', 'drifting', result.earned, false),
      scoreRow('Zones cleared', `${result.zonesCleared} / ${result.zonesTotal}`, result.zoneBonus, true),
      scoreRow('Transitions', String(result.reversals), result.transitionBonus, true),
      scoreRow('Spins', String(result.spins), -result.spinPenalty, true),
      scoreRow('Wall hits', String(result.wallHits), -result.wallPenalty, true),
      ...(route.bells?.length ? [scoreRow('Bells rung', `${result.bells} / ${route.bells.length}`, result.bellBonus, true)] : []),
      totalRow('Final score', result.points.toLocaleString('en-GB')),
    );
  } else {
    rows.push(statRow('Wall hits', String(result.wallHits)));
    if (result.wallHits > 0) rows.push(statRow('Time penalty', `+${(result.wallHits * 2).toFixed(0)}s`));
  }

  if (runGhost) {
    const g = runGhost.row;
    if (currentMode === 'driftRun') {
      const diff = result.points - g.points;
      rows.push(
        statRow(
          `vs ${g.playerName}'s ghost`,
          diff >= 0 ? `Won by ${diff.toLocaleString('en-GB')}` : `Lost by ${(-diff).toLocaleString('en-GB')}`,
        ),
      );
    } else {
      const diff = result.timeSeconds - g.timeMs / 1000;
      rows.push(
        statRow(`vs ${g.playerName}'s ghost`, diff <= 0 ? `Won by ${(-diff).toFixed(2)}s` : `Lost by ${diff.toFixed(2)}s`),
      );
    }
  }

  const best = getBest(route.id, route.version, boardFor(currentMode, currentControls));
  if (best && !isBest) {
    rows.push(
      statRow(
        'Personal best',
        currentMode === 'timeAttack'
          ? formatTime(best.timeSeconds)
          : best.points.toLocaleString('en-GB'),
      ),
    );
  }

  $('result-stats').replaceChildren(...rows);
  resetSubmitPanel();
  const unranked = unrankedReason();
  $('result-tune-note').textContent = unranked ?? '';
  $('result-tune-note').hidden = !unranked;
  $('result-submit').hidden = !!unranked;
  showScreen('results');
  if (!unranked) {
    resultCar = 'category';
    void showResultBoard();
  }
}

/** A line of the score: what it was for, how many, and what it added or took. */
function scoreRow(label: string, detail: string, amount: number, signed: boolean): HTMLElement {
  const el = document.createElement('div');
  el.className = 'score-row';
  const name = document.createElement('span');
  name.className = 'score-row__label';
  name.textContent = label;
  const count = document.createElement('span');
  count.className = 'score-row__detail';
  count.textContent = detail;
  const value = document.createElement('span');
  value.className = 'score-row__value';
  const n = Math.abs(amount).toLocaleString('en-GB');
  value.textContent = !signed ? n : amount > 0 ? `+${n}` : amount < 0 ? `−${n}` : '0';
  if (signed && amount > 0) value.classList.add('score-row__value--plus');
  if (signed && amount < 0) value.classList.add('score-row__value--minus');
  el.append(name, count, value);
  return el;
}

function totalRow(label: string, value: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'score-total';
  const name = document.createElement('span');
  name.className = 'label';
  name.textContent = label;
  const v = document.createElement('span');
  v.className = 'display score-total__value';
  v.textContent = value;
  el.append(name, v);
  return el;
}

/** The top 20 for this route and mode, under the results, with the player's row marked. */
/** Which cars the results board covers, for the car just driven. */
let resultCar: BoardScope = 'category';

function setResultCar(which: BoardScope): void {
  resultCar = which;
  void showResultBoard();
}

async function showResultBoard(fresh = false): Promise<void> {
  const route = currentRoute;
  const container = $('result-board-rows');
  if (!route) return;
  const mode: PageMode = battle?.kind === 'house' ? 'tandem' : currentMode;
  const key = resultBoardKey();
  const scope = resultCar;
  const car = scopeCar(scope);
  $('result-board-title').textContent = `Top 20 · ${boardTitle(key)}`;
  paintScope('result', scope);
  container.replaceChildren(caption('Loading…'));
  try {
    const board = await fetchBoard(route.id, key, car, route.version, SIM_VERSION, fresh);
    // The player may have switched boards while this was in flight.
    if (scope !== resultCar) return;
    container.replaceChildren(
      ...(board.rows.length === 0
        ? [caption(emptyBoard(scope, 'scores'))]
        : board.rows.map((row) => boardRow(row, mode, key))),
    );
  } catch (err) {
    container.replaceChildren(
      caption(err instanceof LeaderboardError ? err.message : 'Could not reach the leaderboard.'),
    );
  }
}

function quitRun(): void {
  thumb.releaseAll();
  pedals.releaseAll();
  proPedals.releaseAll();
  tunePanel.close();
  session?.abort();
  session = null;
  $('overlay-countdown').hidden = true;
  $('overlay-paused').hidden = true;
  buildRouteList();
  showScreen('routes');
}

// --- Posting a score --------------------------------------------------------

/** This result's id for the server: kept across retries, so it is stored once. */
let submissionId = '';

function resetSubmitPanel(): void {
  submissionId = crypto.randomUUID();
  const input = $<HTMLInputElement>('player-name');
  input.value = settings.playerName;
  input.removeAttribute('aria-invalid');
  $('name-error').textContent = '';
  $('post-status').textContent = '';
  delete $('post-status').dataset.top;
  const post = $<HTMLButtonElement>('btn-post');
  post.disabled = false;
  post.textContent = 'Post score';
}

/** Live feedback as the player types. The server check is the one that counts. */
function validateNameField(): boolean {
  const input = $<HTMLInputElement>('player-name');
  const error = $('name-error');
  const value = input.value.trim();
  if (value.length === 0) {
    input.removeAttribute('aria-invalid');
    error.textContent = '';
    return false;
  }
  const check = checkName(value);
  input.setAttribute('aria-invalid', String(!check.ok));
  error.textContent = check.ok ? '' : (check.message ?? '');
  return check.ok;
}

async function postScore(): Promise<void> {
  const route = currentRoute;
  const outcome = lastOutcome;
  const status = $('post-status');
  const post = $<HTMLButtonElement>('btn-post');

  if (!route || !outcome) {
    status.textContent = 'Nothing to post.';
    return;
  }
  if (!validateNameField()) {
    $('player-name').focus();
    if (!$('name-error').textContent) $('name-error').textContent = 'Pick a name first.';
    return;
  }

  const name = $<HTMLInputElement>('player-name').value.trim();
  settings.playerName = name;
  saveSettings(settings);

  post.disabled = true;
  post.textContent = 'Posting…';
  status.textContent = '';

  const car = carById(settings.carId);
  const { result } = outcome;

  try {
    // The replay rides along with the score. A gzipped 90-second run is a few
    // KB, which is small enough to send for every submission rather than only
    // for a record -- and it is what makes ghost playback possible later.
    let replay: string | undefined;
    try {
      // A battle has two runs and a computer driver; there is no one ghost to keep.
      if (!battle) replay = bytesToBase64(await compress(outcome.recorder.encode()));
    } catch {
      // Compression unavailable: post the score without a ghost.
    }

    const response = await submitScore({
      routeId: route.id,
      routeVersion: route.version,
      mode: resultBoardKey(),
      simVersion: SIM_VERSION,
      playerName: name,
      carId: car.id,
      timeMs: battle ? battle.timeMs : Math.round(result.timeSeconds * 1000),
      points: battle ? battle.youChase + battle.youLead : result.points,
      grade: battle ? (battle.outcome === 'win' ? 'W' : 'L') : result.grade,
      // No throttle assist any more: the car always drives itself. The field
      // stays in the API so the worker and database need no migration.
      assist: 1,
      tickCount: result.totalTicks,
      replay,
      submissionId,
    });

    myLastRowId = response.id;
    post.textContent = 'Posted';
    // The rank is on the category board, the one shown under the result.
    const category = categoryOf(car.id)?.name ?? 'the top 20';
    status.textContent = response.inTop20
      ? `You're #${response.rank} in ${category}.`
      : `You're #${response.rank} in ${category} — the top 20 make the board.`;
    status.dataset.top = String(response.inTop20);
    void showResultBoard(true);
  } catch (err) {
    post.disabled = false;
    post.textContent = 'Post score';
    if (err instanceof LeaderboardError) {
      status.textContent = err.message;
      // A name the server rejects should land on the field, not in a status line.
      if (err.code === 'profanity' || err.code === 'reserved') {
        $('name-error').textContent = err.message;
        $('player-name').setAttribute('aria-invalid', 'true');
        status.textContent = '';
      }
    } else {
      status.textContent = 'Could not post that score.';
    }
  }
}

// --- Wiring -----------------------------------------------------------------

$('btn-drive').addEventListener('click', () => {
  buildRouteList();
  showScreen('routes');
});
$('btn-garage').addEventListener('click', () => openGarage('title'));
$('btn-intro-car').addEventListener('click', () => openGarage('intro'));
$('poster-art').addEventListener('click', openPosterZoom);
$('intro-car-sprite').addEventListener('click', () => openCarZoom(carById(settings.carId)));
$('intro-car-sprite').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') openCarZoom(carById(settings.carId));
});
$('poster-art').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') openPosterZoom();
});
// A tap closes it; a drag only looks around (a drag does not produce a click).
$('poster-lightbox').addEventListener('click', closePosterZoom);
$('btn-settings').addEventListener('click', () => showScreen('settings'));
$('btn-sound-lab').addEventListener('click', () => {
  for (const key of Object.keys(SCREENS) as ScreenName[]) SCREENS[key].hidden = true;
  soundLab.open(carById(settings.carId).engine);
});
$('btn-routes-back').addEventListener('click', () => showScreen('title'));
for (const b of $('garage-filter').querySelectorAll<HTMLElement>('[data-filter]')) {
  b.addEventListener('click', () => {
    garageFilter = b.dataset.filter as GarageFilter;
    buildCarList();
  });
}
for (const b of $('garage-sort').querySelectorAll<HTMLElement>('[data-sort]')) {
  b.addEventListener('click', () => {
    garageSort = b.dataset.sort as GarageSort;
    buildCarList();
  });
}
$('btn-garage-back').addEventListener('click', leaveGarage);
$('btn-settings-back').addEventListener('click', () => showScreen('title'));
$('btn-intro-back').addEventListener('click', () => {
  buildRouteList();
  showScreen('routes');
});
$('btn-time-attack').addEventListener('click', () => setBoardMode('timeAttack'));
$('btn-drift-run').addEventListener('click', () => setBoardMode('driftRun'));
$('btn-tandem').addEventListener('click', () => setBoardMode('tandem'));
$('btn-go').addEventListener('click', () => {
  if (boardMode === 'tandem') startHouseBattle();
  else void startRun(boardMode);
});
$('btn-ghost-clear').addEventListener('click', () => setGhostPick(null));
$('btn-board-category').addEventListener('click', () => setBoardCar('category'));
$('btn-board-car').addEventListener('click', () => setBoardCar('car'));
$('btn-board-all').addEventListener('click', () => setBoardCar('all'));
$('btn-level-easy').addEventListener('click', () => setTimeAttackLevel('easy'));
$('btn-level-pro').addEventListener('click', () => setTimeAttackLevel('pro'));
$('btn-retry').addEventListener('click', retry);
$('btn-result-car-change').addEventListener('click', () => openGarage('rerun'));
$('btn-result-category').addEventListener('click', () => setResultCar('category'));
$('btn-result-car').addEventListener('click', () => setResultCar('car'));
$('btn-result-all').addEventListener('click', () => setResultCar('all'));
$('btn-result-routes').addEventListener('click', () => {
  buildRouteList();
  showScreen('routes');
});
$('btn-result-title').addEventListener('click', () => showScreen('title'));
$('btn-post').addEventListener('click', () => void postScore());
$('player-name').addEventListener('input', validateNameField);
$('btn-quit').addEventListener('click', quitRun);
$('btn-resume').addEventListener('click', resumeRun);
$('btn-restart').addEventListener('click', restartRun);

// The pause button: a tap pauses; holding it fills a ring round the button
// and restarts the run when the ring closes. The run carries on meanwhile, so
// a restart costs no more than the second it takes. (Must match the ring's
// animation in game.css.)
const RESTART_HOLD_MS = 1000;
const pauseBtn = $('btn-pause');
let restartTimer = 0;
let holdPointer: number | null = null;

function endHold(): void {
  window.clearTimeout(restartTimer);
  restartTimer = 0;
  holdPointer = null;
  pauseBtn.classList.remove('pause-btn--holding');
  gameEl.classList.remove('restart-holding');
}

pauseBtn.addEventListener('pointerdown', (e) => {
  if (!isRunActive() || holdPointer !== null) return;
  e.preventDefault();
  holdPointer = e.pointerId;
  try {
    pauseBtn.setPointerCapture(e.pointerId);
  } catch {
    // Synthetic pointer; the hold still works.
  }
  pauseBtn.classList.add('pause-btn--holding');
  gameEl.classList.add('restart-holding');
  restartTimer = window.setTimeout(() => {
    endHold();
    restartRun();
  }, RESTART_HOLD_MS);
});
pauseBtn.addEventListener('pointerup', (e) => {
  if (e.pointerId !== holdPointer) return;
  // Let go before the ring closed: that was a tap, or a change of mind.
  endHold();
  pauseRun();
});
pauseBtn.addEventListener('pointercancel', (e) => {
  if (e.pointerId === holdPointer) endHold();
});
pauseBtn.addEventListener('contextmenu', (e) => e.preventDefault());
// Keyboard activation still pauses; pointer taps are handled above.
pauseBtn.addEventListener('click', (e) => {
  if (e.detail === 0) pauseRun();
});

/** Start the same run again from the line: same route, mode, car and ghost. */
function restartRun(): void {
  if (!isRunActive()) return;
  endHold();
  thumb.releaseAll();
  pedals.releaseAll();
  proPedals.releaseAll();
  input.releaseAll();
  session?.abandon();
  session = null;
  $('overlay-paused').hidden = true;
  if (battle) startBattleRun(true);
  else void startRun(currentMode, true);
}

function isRunActive(): boolean {
  return !gameEl.hidden && !!session && session.phase !== 'finished' && session.phase !== 'aborted';
}

function pauseRun(): void {
  if (!isRunActive() || !$('overlay-paused').hidden) return;
  session?.stop();
  // A thumb resting on the screen must not come back steering on resume.
  thumb.releaseAll();
  pedals.releaseAll();
  proPedals.releaseAll();
  $('overlay-paused').hidden = false;
}

function resumeRun(): void {
  if (!isRunActive() || $('overlay-paused').hidden) return;
  $('overlay-paused').hidden = true;
  session?.start();
}

// --- Tuning -----------------------------------------------------------------

const tunePanel = new TunePanel($('tune-panel'), {
  getSensitivity: () => settings.steerSensitivity,
  setSensitivity,
  onClose: closeTuning,
});

function openTuning(): void {
  if (!isRunActive() || tunePanel.isOpen) return;
  session?.stop();
  thumb.releaseAll();
  $('overlay-paused').hidden = true;
  pedals.releaseAll();
  proPedals.releaseAll();
  tunePanel.open(carById(settings.carId), currentMode, currentControls);
}

function closeTuning(): void {
  if (!tunePanel.isOpen) return;
  tunePanel.close();
  if (isRunActive()) session?.start();
}

$('btn-tune').addEventListener('click', openTuning);
// Tuning is parked: its Settings row only appears when #tune or ?tune is on
// the URL, and a setting left on from before does not bring it back.
setTuneMode(isTuneMode());
$('tune-row').hidden = !tuneMode;
window.addEventListener('hashchange', () => {
  if (isTuneMode() && !tuneMode && !isRunActive()) {
    setTuneMode(true);
    $('tune-row').hidden = false;
    syncTuneToggle();
  }
});

window.addEventListener('keydown', (e) => {
  // Using a bound key means the keyboard is in use, whatever the device.
  if (input.isBound(e.code)) setInputMode('keyboard');

  if (e.code !== 'Escape' || listeningFor || !isRunActive()) return;
  e.preventDefault();
  if (tunePanel.isOpen) closeTuning();
  else if ($('overlay-paused').hidden) pauseRun();
  else resumeRun();
});

// Losing the app to a call or a notification pauses rather than leaving the
// car driving unattended into a wall.
document.addEventListener('visibilitychange', () => {
  if (document.hidden && !tunePanel.isOpen) pauseRun();
});

window.addEventListener('resize', () => {
  if (!gameEl.hidden) renderer.resize();
  if (!SCREENS.intro.hidden && currentRoute) showPoster(currentRoute);
});

// iOS reports a rotation before the page has its new size. Measure again once it
// has settled; the renderer also watches its own size, this is belt and braces.
window.addEventListener('orientationchange', () => {
  for (const ms of [100, 400, 900]) {
    window.setTimeout(() => {
      if (!gameEl.hidden) renderer.resize();
    }, ms);
  }
});

// --- No zooming (iOS) ---------------------------------------------------------
//
// iOS ignores the page's "no zoom" setting on purpose, for accessibility, so a
// quick double tap on a pad or two thumbs landing together could zoom the page
// or open the magnifier loupe mid-run. Pinch and double-tap are cancelled here,
// and on the race screen a touch that lands on the driving surfaces is claimed
// outright, which stops the loupe too. Pointer events -- what the controls
// actually listen to -- still arrive; only the browser's own gestures do not.
for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(type, (e) => e.preventDefault(), { passive: false });
}
document.addEventListener('dblclick', (e) => e.preventDefault(), { passive: false });
document.addEventListener(
  'touchmove',
  (e) => {
    if (e.touches.length > 1) e.preventDefault();
  },
  { passive: false },
);
gameEl.addEventListener(
  'touchstart',
  (e) => {
    // Buttons (pause, the pause menu) and the tuning sliders still need their
    // taps to become clicks and drags.
    if (e.target instanceof Element && e.target.closest('button, input, select, label, a')) return;
    e.preventDefault();
  },
  { passive: false },
);

// A real touch anywhere switches to touch hints.
window.addEventListener(
  'pointerdown',
  (e) => {
    if (e.pointerType === 'touch') setInputMode('touch');
  },
  { capture: true },
);

// Bests already on this device count towards the unlock challenges. A car
// can only be selected unlocked, but a device that somehow has a locked one
// selected goes back to the first car.
creditPastRuns();
if (isLocked(settings.carId)) {
  settings.carId = CARS[0].id;
  saveSettings(settings);
}
buildRouteList();
buildCarList();
buildSettings();
setBoardMode('timeAttack');
syncKeyHints();
showScreen('title');
setUpSeasonCard();

/**
 * The season's card: over the title screen by itself once per device, and
 * under "What’s new" for as long as the season lasts. See shared/season.ts.
 */
function setUpSeasonCard(): void {
  const season = activeSeason();
  const card = document.getElementById('season-card') as HTMLDialogElement | null;
  if (!season || !card || typeof card.showModal !== 'function') return;
  const g = season.greeting;
  $('season-kicker').textContent = g.kicker;
  $('season-title').textContent = g.title;
  $('season-lede').textContent = g.lede;
  $('season-news').replaceChildren(
    ...g.news.map((n) => {
      const li = document.createElement('li');
      const head = document.createElement('b');
      head.textContent = n.head;
      li.append(head, n.body);
      return li;
    }),
  );
  $('btn-season-close').textContent = g.button;
  // Seen once it is closed, however: the button, Escape, or the back gesture.
  card.addEventListener('close', () => markGreetingSeen(season));
  const news = $('btn-news');
  news.hidden = false;
  news.addEventListener('click', () => card.showModal());
  if (greetingDue()) card.showModal();
}

// Expose live state for tuning from the console: camera framing and handling
// are judged by eye, and being able to read the actual numbers behind a frame
// is the difference between tuning and guessing.
Object.assign(window as unknown as Record<string, unknown>, {
  idrift: {
    get settings() {
      return settings;
    },
    get outcome() {
      return lastOutcome;
    },
    get session() {
      return session;
    },
    get route() {
      return currentRoute;
    },
    renderer,
    TICK_RATE,
    SIM_VERSION,
  },
});
